import _ from "lodash";

/**
 * Canonical pitch-class table, indexed by pitch class (A = 0). These dual names are only for
 * contexts with no key (naming an open string in a tuning). Anything with a root or key spells
 * its notes properly via `spellFromRoot` instead.
 */
export const NOTE_NAMES = [
  "A",
  "A#/Bb",
  "B",
  "C",
  "C#/Db",
  "D",
  "D#/Eb",
  "E",
  "F",
  "F#/Gb",
  "G",
  "G#/Ab",
];

export const PITCH_CLASS_COUNT = 12;

// Note letters with their natural pitch classes, in the same A = 0 space as NOTE_NAMES.
const LETTERS = ["A", "B", "C", "D", "E", "F", "G"];
const LETTER_PITCH_CLASSES = [0, 2, 3, 5, 7, 8, 10];

// The sharp and flat spellings of each pitch class. Sharp first: it wins ties in
// chooseRootSpelling (F# major over Gb major).
const SPELLINGS_BY_PITCH_CLASS: string[][] = [
  ["A"],
  ["A#", "Bb"],
  ["B"],
  ["C"],
  ["C#", "Db"],
  ["D"],
  ["D#", "Eb"],
  ["E"],
  ["F"],
  ["F#", "Gb"],
  ["G"],
  ["G#", "Ab"],
];

/** A spelled note name split into its letter and accidental offset (#: +1 each, b: -1 each). */
function parseSpelling(name: string): { letterIndex: number; accidental: number } {
  const match = /^([A-G])(#{1,2}|b{1,2})?$/.exec(name);
  if (!match) {
    throw new Error(`Invalid note name: ${name}`);
  }
  const accidentals = match[2] ?? "";
  return {
    letterIndex: LETTERS.indexOf(match[1]),
    accidental: accidentals.startsWith("#") ? accidentals.length : -accidentals.length,
  };
}

/**
 * The pitch class (A = 0) of a spelled note name ("Bb", "F#", "Cb"). Also accepts the canonical
 * dual form ("A#/Bb") so NOTE_NAMES entries compare cleanly.
 */
export function pitchClassOf(name: string): number {
  const { letterIndex, accidental } = parseSpelling(name.split("/")[0]);
  return (LETTER_PITCH_CLASSES[letterIndex] + accidental + PITCH_CLASS_COUNT) % PITCH_CLASS_COUNT;
}

/**
 * Spell the note `semitone` half steps above the root, on the letter `degree` scale steps up —
 * the rule that makes the third of F# major an A#, never a Bb. Throws when no spelling within a
 * double accidental exists (a sign the root spelling doesn't suit the chord/scale).
 */
export function spellFromRoot(rootSpelling: string, degree: number, semitone: number): string {
  const root = parseSpelling(rootSpelling);
  const letterIndex = (root.letterIndex + degree - 1) % LETTERS.length;
  const pitchClass = (pitchClassOf(rootSpelling) + semitone) % PITCH_CLASS_COUNT;
  let accidental =
    (pitchClass - LETTER_PITCH_CLASSES[letterIndex] + PITCH_CLASS_COUNT) % PITCH_CLASS_COUNT;
  if (accidental > 6) accidental -= PITCH_CLASS_COUNT;
  if (Math.abs(accidental) > 2) {
    throw new Error(`Cannot spell degree ${degree} of ${rootSpelling} within a double accidental`);
  }
  return (
    LETTERS[letterIndex] + (accidental >= 0 ? "#".repeat(accidental) : "b".repeat(-accidental))
  );
}

/** A note name for display: ASCII accidentals swapped for ♯/♭ ("Bb" → "B♭", "A#/Bb" → "A♯/B♭"). */
export function formatNoteName(name: string): string {
  return name
    .split("/")
    .map((part) => part[0] + part.slice(1).replace(/#/g, "♯").replace(/b/g, "♭"))
    .join("/");
}

const STANDARD_TUNING = ["E", "A", "D", "G", "B", "E"];

/**
 * Assume the guitar has six strings. Actual tuning will be reversed of rawTuning due to the standard
 * guitar notation (i.e. first string is the highest string rather than the lowest string).
 */
export function getGuitarPitchClass(
  stringNum: number,
  fretNum: number,
  rawTuning: string[] = STANDARD_TUNING
): number {
  if (stringNum < 1 || stringNum > 6) {
    throw new Error("Invalid string number");
  } else if (fretNum < 0) {
    throw new Error("Fret number cannot be negative");
  }
  const tuning = [...rawTuning].reverse();
  if (tuning.length !== 6 || !tuning.every((note) => NOTE_NAMES.includes(note))) {
    throw new Error(`Invalid tuning; each note must be one of ${NOTE_NAMES}`);
  }
  const stringPitchClass = NOTE_NAMES.indexOf(tuning[stringNum - 1]);
  return (stringPitchClass + fretNum) % PITCH_CLASS_COUNT;
}

/** A single fretted note: where it sits and which scale degree it represents. */
export interface NotePosition {
  stringNum: number;
  fretNum: number;
  degree: number;
  noteName: string;
}

export const ALL_STRINGS = [1, 2, 3, 4, 5, 6];

/**
 * A single tone of a scale, tying together the three facts that must stay in sync: its semitone
 * offset from the root, the scale degree it represents, and how that degree is written. Each scale
 * is defined as one `ToneSpec[]` so these can't drift apart, with the records the rest of the code
 * consumes derived from it via the helpers below.
 */
export interface ToneSpec {
  semitone: number;
  degree: number;
  label: string;
}

/**
 * The root spelling of the given pitch class that suits this scale: the candidate whose spelled
 * tones take the fewest accidentals (no double accidentals above all), ties to the sharp name.
 * This is what picks Bb major but C# minor, and F# major over Gb major.
 */
export function chooseRootSpelling(pitchClass: number, tones: ToneSpec[]): string {
  // A double accidental outweighs any number of singles.
  const score = (candidate: string): number => {
    let accidentals = 0;
    for (const tone of tones) {
      let spelled;
      try {
        spelled = spellFromRoot(candidate, tone.degree, tone.semitone);
      } catch {
        return Infinity;
      }
      const count = spelled.length - 1;
      accidentals += count > 1 ? 100 : count;
    }
    return accidentals;
  };
  return _.minBy(SPELLINGS_BY_PITCH_CLASS[pitchClass], score) as string;
}

/** The notes of a `ToneSpec[]` scale spelled from a root, each paired with its degree. */
export function tonesToNotes(
  rootSpelling: string,
  tones: ToneSpec[]
): { noteName: string; degree: number }[] {
  return tones.map((t) => ({
    noteName: spellFromRoot(rootSpelling, t.degree, t.semitone),
    degree: t.degree,
  }));
}

/** The degree → label map a `ToneSpec[]` scale defines. */
export function tonesToLabels(tones: ToneSpec[]): Record<number, string> {
  const labels: Record<number, string> = {};
  for (const { degree, label } of tones) {
    labels[degree] = label;
  }
  return labels;
}

/** Every fretted occurrence of the given notes across the strings and fret range. */
export function findAllInRange(
  strings: number[],
  startFret: number,
  endFret: number,
  notes: { noteName: string; degree: number }[]
): NotePosition[] {
  const byPitchClass = new Map(notes.map((n) => [pitchClassOf(n.noteName), n]));
  const positions: NotePosition[] = [];
  for (const stringNum of strings) {
    for (let fretNum = startFret; fretNum <= endFret; fretNum++) {
      const note = byPitchClass.get(getGuitarPitchClass(stringNum, fretNum));
      if (note) {
        positions.push({ stringNum, fretNum, degree: note.degree, noteName: note.noteName });
      }
    }
  }
  return positions;
}
